import { useEffect, useState, useCallback, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getInstalledInfo } from "@/lib/version";
import { Capacitor } from "@capacitor/core";
import { Download, AlertTriangle, Loader2 } from "lucide-react";
import type { AppUpdateSettings } from "@/lib/settings";

function canUseNativeAppPlugin(): boolean {
  try {
    return (
      Capacitor.isNativePlatform() &&
      typeof (Capacitor as any).isPluginAvailable === "function" &&
      (Capacitor as any).isPluginAvailable("App") === true
    );
  } catch {
    return false;
  }
}

// NOTE: We must NEVER return the `@capacitor/app` `App` proxy from an async
// function. The returned promise gets unwrapped by JS, which reads `.then`
// on the value — and any property read on a Capacitor plugin Proxy is
// dispatched as a native method call. That triggers the runtime error
// "App.then() is not implemented on android". Import it statically and
// only await real plugin methods (addListener, getInfo, …).


const DEFAULT_MSG = "New update available. Please update the app to continue.";

type Snapshot = {
  installedVersion: string | null;
  installedBuild: number | null;
  settings: AppUpdateSettings | null;
  sourceKey: "app_update";
  fetchedAt: string | null;
  decision: "ALLOWED" | "BLOCKED" | "PENDING";
  reason: string;
};

type CheckState =
  | { status: "loading"; snapshot: Snapshot }
  | { status: "allowed"; snapshot: Snapshot }
  | { status: "blocked"; snapshot: Snapshot };

const EMPTY_SNAPSHOT: Snapshot = {
  installedVersion: null,
  installedBuild: null,
  settings: null,
  sourceKey: "app_update",
  fetchedAt: null,
  decision: "PENDING",
  reason: "initializing",
};

function readString(value: Record<string, unknown>, snake: string, camel: string, fallback = "") {
  const raw = value[snake] ?? value[camel];
  return raw == null ? fallback : String(raw);
}

function readInt(value: Record<string, unknown>, snake: string, camel: string) {
  const raw = value[snake] ?? value[camel];
  const n = typeof raw === "number" ? raw : parseInt(String(raw ?? ""), 10);
  return Number.isFinite(n) ? n : 0;
}

function readBool(value: Record<string, unknown>, snake: string, camel: string) {
  const raw = value[snake] ?? value[camel];
  if (typeof raw === "boolean") return raw;
  if (typeof raw === "string") return raw.toLowerCase() === "true";
  return Boolean(raw);
}

async function fetchAppUpdate(): Promise<AppUpdateSettings | null> {
  try {
    const { data, error } = await supabase
      .from("app_settings")
      .select("value")
      .eq("key", "app_update")
      .maybeSingle();
    if (error) throw error;
    const v = (data?.value ?? {}) as Record<string, unknown>;
    return {
      current_app_version: readString(v, "current_app_version", "currentAppVersion"),
      minimum_required_version: readString(v, "minimum_required_version", "minimumRequiredVersion"),
      current_version_code: readInt(v, "current_version_code", "currentVersionCode"),
      minimum_required_version_code: readInt(v, "minimum_required_version_code", "minimumRequiredVersionCode"),
      latest_apk_download_link: readString(v, "latest_apk_download_link", "latestApkDownloadLink"),
      force_update_enabled: readBool(v, "force_update_enabled", "forceUpdateEnabled"),
      update_message: readString(v, "update_message", "updateMessage", DEFAULT_MSG),
    };
  } catch (e) {
    console.warn("[force-update] fetch failed", e);
    return null;
  }
}

async function decide(): Promise<CheckState> {
  const isNative = Capacitor.isNativePlatform();
  const [settings, info] = await Promise.all([fetchAppUpdate(), getInstalledInfo()]);
  const snapshot: Snapshot = {
    installedVersion: info.version,
    installedBuild: info.build,
    settings,
    sourceKey: "app_update",
    fetchedAt: new Date().toISOString(),
    decision: "PENDING",
    reason: "",
  };

  console.info("[force-update] check", {
    native: isNative,
    installedVersion: info.version,
    installedBuild: info.build,
    settings,
  });

  if (!isNative) {
    return { status: "allowed", snapshot: { ...snapshot, decision: "ALLOWED", reason: "non-native platform" } };
  }
  if (!settings) {
    return { status: "allowed", snapshot: { ...snapshot, decision: "ALLOWED", reason: "no settings row" } };
  }
  if (!settings.force_update_enabled) {
    return { status: "allowed", snapshot: { ...snapshot, decision: "ALLOWED", reason: "force update disabled" } };
  }

  const minCode = settings.minimum_required_version_code || 0;
  if (minCode <= 0) {
    return { status: "allowed", snapshot: { ...snapshot, decision: "ALLOWED", reason: "minimum_required_version_code is not configured" } };
  }

  if (info.build == null) {
    const reason = `installedBuild unavailable — App.getInfo() failed. Allowing to avoid false block; user will see "Unable to verify app version".`;
    console.warn("[force-update] ALLOWED (unverified)", reason);
    return { status: "allowed", snapshot: { ...snapshot, decision: "ALLOWED", reason } };
  }

  const latestCode = settings.current_version_code || 0;
  const outdated = info.build < minCode;
  const reason = `installedBuild=${info.build}, minRequired=${minCode}, latest=${latestCode} → ${outdated ? "BLOCKED" : "ALLOWED"}`;

  if (outdated) {
    console.warn("[force-update] BLOCKED", reason);
    return { status: "blocked", snapshot: { ...snapshot, decision: "BLOCKED", reason } };
  }
  console.info("[force-update] ALLOWED", reason);
  return { status: "allowed", snapshot: { ...snapshot, decision: "ALLOWED", reason } };
}

function DebugPanel({ snapshot }: { snapshot: Snapshot }) {
  const [open, setOpen] = useState(snapshot.decision === "BLOCKED");
  const s = snapshot.settings;
  // Hide the debug badge from end users. Only render in dev builds or when
  // explicitly toggled via `window.__BC_FORCE_UPDATE_DEBUG = true`.
  const debugEnabled =
    (typeof import.meta !== "undefined" && (import.meta as any).env?.DEV) ||
    (typeof window !== "undefined" && (window as any).__BC_FORCE_UPDATE_DEBUG === true);
  if (!debugEnabled) return null;
  return (
    <div className="fixed bottom-2 right-2 z-[10000] max-w-[92vw] text-[10px] font-mono">
      <button
        onClick={() => setOpen((v) => !v)}
        className={`rounded px-2 py-1 text-white shadow ${
          snapshot.decision === "BLOCKED" ? "bg-destructive" : snapshot.decision === "ALLOWED" ? "bg-emerald-600" : "bg-slate-600"
        }`}
      >
        decision: {snapshot.decision}
      </button>
      {open && (
        <div className="mt-1 rounded bg-black/85 p-2 text-white leading-tight space-y-0.5">
          <div>installedVersion: {snapshot.installedVersion ?? "null"}</div>
          <div>installedBuild: {snapshot.installedBuild ?? "null"}</div>
          <div>minimumRequiredVersion: {s?.minimum_required_version || "—"}</div>
          <div>minimumRequiredBuild: {s?.minimum_required_version_code ?? 0}</div>
          <div>forceUpdateEnabled: {String(!!s?.force_update_enabled)}</div>
          <div>decision: {snapshot.decision}</div>
          <div>sourceKey: {snapshot.sourceKey}</div>
          <div>fetchedAt: {snapshot.fetchedAt ?? "null"}</div>
          <div>reason: {snapshot.reason}</div>
          <div>native: {String(Capacitor.isNativePlatform())}</div>
        </div>
      )}
    </div>
  );
}

export function ForceUpdateGate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<CheckState>({ status: "loading", snapshot: EMPTY_SNAPSHOT });

  const runCheck = useCallback(async () => {
    const next = await decide();
    setState(next);
  }, []);

  useEffect(() => {
    void runCheck();
    if (!canUseNativeAppPlugin()) return;
    let cleanup: (() => void) | undefined;
    let cancelled = false;
    void (async () => {
      let App: typeof import("@capacitor/app").App;
      try {
        ({ App } = await import("@capacitor/app"));
      } catch (e) {
        console.warn("[force-update] @capacitor/app import failed:", e);
        return;
      }
      if (cancelled) return;
      const handles: Array<{ remove: () => void }> = [];
      try {
        handles.push(await App.addListener("appStateChange", (s: { isActive: boolean }) => { if (s.isActive) void runCheck(); }));
      } catch (e) { console.warn("[force-update] appStateChange listener failed:", e); }
      try {
        handles.push(await App.addListener("resume", () => { void runCheck(); }));
      } catch (e) { console.warn("[force-update] resume listener failed:", e); }
      cleanup = () => {
        handles.forEach((h) => { try { h.remove(); } catch { /* noop */ } });
      };
      if (cancelled) cleanup();
    })();
    return () => { cancelled = true; cleanup?.(); };
  }, [runCheck]);

  // Trap hardware back button whenever blocked or still loading on native.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    (window as any).__BC_FORCE_UPDATE_BLOCKED = state.status !== "allowed";
    if (state.status === "allowed") return;
    if (!canUseNativeAppPlugin()) return;
    let sub: { remove: () => void } | null = null;
    let cancelled = false;
    void (async () => {
      let App: typeof import("@capacitor/app").App;
      try {
        ({ App } = await import("@capacitor/app"));
      } catch (e) {
        console.warn("[force-update] @capacitor/app import failed:", e);
        return;
      }
      if (cancelled) return;
      try {
        sub = await App.addListener("backButton", () => { /* trap */ });
        if (cancelled) { try { sub?.remove(); } catch { /* noop */ } }
      } catch (e) {
        console.warn("[force-update] backButton listener failed:", e);
      }
    })();
    return () => {
      cancelled = true;
      (window as any).__BC_FORCE_UPDATE_BLOCKED = false;
      try { sub?.remove(); } catch { /* noop */ }
    };
  }, [state.status]);

  const isNative = Capacitor.isNativePlatform();

  if (state.status === "loading") {
    // Never block first paint. Render the app immediately and let the
    // update check run in the background — if it later decides the build
    // is too old, the blocking overlay mounts on top.
    return (
      <>
        {children}
        {isNative && <DebugPanel snapshot={state.snapshot} />}
      </>
    );
  }

  if (state.status === "allowed") {
    return (
      <>
        {children}
        {isNative && <DebugPanel snapshot={state.snapshot} />}
      </>
    );
  }

  const { snapshot } = state;
  const settings = snapshot.settings!;
  const link = settings.latest_apk_download_link || "";
  const message = settings.update_message?.trim() || DEFAULT_MSG;
  const minLabel =
    settings.minimum_required_version_code > 0
      ? `${settings.minimum_required_version || ""} (code ${settings.minimum_required_version_code})`
      : settings.minimum_required_version || "—";
  const installedLabel =
    snapshot.installedBuild != null
      ? `${snapshot.installedVersion ?? "?"} (code ${snapshot.installedBuild})`
      : (snapshot.installedVersion ?? "unknown");

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-background p-6"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="w-full max-w-sm rounded-2xl bg-card p-6 text-center shadow-2xl">
        <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-primary/10 text-primary">
          <AlertTriangle className="h-7 w-7" />
        </div>
        <h2 className="mt-4 text-lg font-black">Update Required</h2>
        <p className="mt-2 text-sm text-muted-foreground">{message}</p>
        <p className="mt-1 text-[11px] text-muted-foreground">
          Installed: {installedLabel} • Required: {minLabel}
        </p>
        <a
          href={link || "#"}
          target={link ? "_blank" : undefined}
          rel="noreferrer"
          onClick={(e) => { if (!link) e.preventDefault(); }}
          className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-bold text-primary-foreground shadow-lg"
        >
          <Download className="h-4 w-4" />
          Update Now
        </a>
        {!link && (
          <p className="mt-2 text-[11px] text-destructive">
            Download link not configured.
          </p>
        )}
      </div>
      <DebugPanel snapshot={snapshot} />
    </div>
  );
}
